import type { Notification } from '@/types/notification.types';
import {
  dashboardRoutePaths,
  financeRoutePaths,
  inventoryRoutePaths,
  materialStockPath,
  queueRoutePaths,
  settingsSectionPath,
} from '@/routes/route-config';
import { mentionPath } from './mention-links';
import { xrayUploadPath } from './xray-links';

type Linkable = Pick<
  Notification,
  'type' | 'resourceType' | 'resourceId' | 'i18n'
>;

const reservationPath = (id: string) =>
  dashboardRoutePaths.reservationDetails.replace(':appointmentId', id);

const listWithStatus = (status: string) =>
  `${dashboardRoutePaths.reservationsList}?status=${status}&range=all`;

/**
 * Where the record a notice names lives, from its resource alone. Used for
 * types without a rule of their own (system messages, older types) and as
 * the fallback when a typed rule has no id to open.
 */
function resourcePath(n: Linkable): string | null {
  const id = n.resourceId;
  switch (n.resourceType) {
    case 'reservation':
      return id ? reservationPath(id) : dashboardRoutePaths.reservations;
    case 'patient':
      return id
        ? dashboardRoutePaths.patientProfile.replace(':patientId', id)
        : dashboardRoutePaths.patients;
    case 'lab_request':
      return id
        ? dashboardRoutePaths.labRequestDetails.replace(':labRequestId', id)
        : dashboardRoutePaths.labRequests;
    case 'material':
      return id ? materialStockPath(id) : inventoryRoutePaths.stockLevels;
    case 'expense':
      return id
        ? financeRoutePaths.expenseDetails.replace(':expenseId', id)
        : financeRoutePaths.expenses;
    case 'stock_transfer':
      return id
        ? `${inventoryRoutePaths.transfers}?id=${id}`
        : inventoryRoutePaths.transfers;
    case 'chat_conversation':
      return id
        ? `${dashboardRoutePaths.chat}?c=${id}`
        : dashboardRoutePaths.chat;
    case 'feature_request':
      return id
        ? dashboardRoutePaths.feedbackDetail.replace(':id', id)
        : dashboardRoutePaths.feedback;
    case 'queue_item':
    case 'branch':
      return queueRoutePaths.receptionist;
    case 'treatment_item':
      return mentionPath(n);
    default:
      return null;
  }
}

/**
 * The page a notification opens: the most actionable place for its type,
 * then the record it names. Every notice resolves to somewhere, at worst the
 * full notifications list, so a click is never a dead end.
 *
 * One rule for the bell, the Notifications page, toasts and push clicks.
 */
export function notificationPath(n: Linkable): string {
  return typedPath(n) ?? resourcePath(n) ?? dashboardRoutePaths.notifications;
}

function typedPath(n: Linkable): string | null {
  const id = n.resourceId;
  switch (n.type) {
    case 'invitation_received':
      return '/invitations';
    case 'invitation_accepted':
    case 'invitation_declined':
      return settingsSectionPath('staff');

    case 'pending_appointment_approval':
    case 'appointment_request_expiring':
      return dashboardRoutePaths.pendingReservations;
    // Expired requests are declined (cancelled) by the cron.
    case 'appointment_request_expired':
      return listWithStatus('CANCELED');
    case 'no_show_summary':
      return listWithStatus('NO_SHOW');
    case 'appointment_request':
    case 'appointment_reminder':
    case 'appointment_approved':
    case 'appointment_rejected':
    case 'reservation_completed':
      return id ? reservationPath(id) : dashboardRoutePaths.reservations;

    case 'patient_called':
    case 'payment_pending':
    case 'receptionist_called':
    case 'treatment_started':
    case 'treatment_paused':
    case 'treatment_resumed':
      return queueRoutePaths.receptionist;

    case 'payment_received':
      // Finance → Payments is gone; the ledger filtered to patient inflows
      // shows the same rows.
      return `${financeRoutePaths.transactions}?direction=in&source=PAYMENT`;

    case 'chat_message':
      return n.resourceType === 'chat_conversation' && id
        ? `${dashboardRoutePaths.chat}?c=${id}`
        : dashboardRoutePaths.chat;
    case 'mention':
      return mentionPath(n);
    case 'xray_uploaded':
      return xrayUploadPath(n);

    case 'low_stock':
    case 'critical_stock':
    case 'out_of_stock':
      return id ? materialStockPath(id) : inventoryRoutePaths.stockLevels;
    case 'stock_transfer':
      return id
        ? `${inventoryRoutePaths.transfers}?id=${id}`
        : inventoryRoutePaths.transfers;

    case 'lab_delivery_late':
    case 'lab_work_arrived':
      return id
        ? dashboardRoutePaths.labRequestDetails.replace(':labRequestId', id)
        : dashboardRoutePaths.labRequests;

    case 'waitlist_slot':
      return id
        ? `${dashboardRoutePaths.waitlist}?slot=${id}`
        : dashboardRoutePaths.waitlist;

    case 'billing_notice':
      return settingsSectionPath('subscription');

    case 'plan_signed_remotely':
      return id
        ? `${dashboardRoutePaths.patientProfile.replace(':patientId', id)}?tab=treatments`
        : dashboardRoutePaths.patients;
    case 'medical_form_submitted':
      return id
        ? `${dashboardRoutePaths.patientProfile.replace(':patientId', id)}?tab=medical-history`
        : dashboardRoutePaths.patients;
    case 'plan_instalments_overdue':
      return `${financeRoutePaths.instalments}?view=overdue`;
    case 'recurring_expense_generated':
      // One expense opens it; several open the list.
      return id
        ? financeRoutePaths.expenseDetails.replace(':expenseId', id)
        : financeRoutePaths.expenses;

    default:
      return null;
  }
}
